import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  LogOut, Upload, Trash2, Loader2, Film, ShieldCheck, Plus, Save, X,
  Tags, Inbox, ClipboardList, Check, RotateCcw, ChevronDown, ChevronUp,
  Users, Crown, Ban, RefreshCw, Home, FileEdit,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIERS = [
  { value: "standard", label: "Standard" },
  { value: "vip", label: "VIP" },
];

const emptyPkg = {
  id: null, name: "", tagline: "", price: "", features: "",
  tier: "standard", sort_order: 99, stripe_link: "",
};

export default function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/custom-orders`);
      setOrders(r.data);
    } catch (err) {
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/custom-orders/${id}?status=${status}`, null);
      toast.success(status === "completed" ? "Marked completed" : "Reopened");
      await load();
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this order? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/custom-orders/${id}`);
      toast.success("Order deleted");
      await load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const inProgress = orders.filter((o) => o.status === "in_progress");
  const completed = orders.filter((o) => o.status === "completed");

  if (loading) return <div className="text-white/40 text-sm">Loading orders…</div>;

  const renderGroup = (title, list, accent, statusBtn) => (
    <div data-testid={`orders-group-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="flex items-center gap-3 mb-4">
        <h2 className="font-display font-bold uppercase text-xl">{title}</h2>
        <span
          className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border"
          style={{ borderColor: accent, color: accent }}
        >
          {list.length}
        </span>
      </div>
      {list.length === 0 ? (
        <div className="rv-card rounded-xl p-8 text-center text-white/40 text-sm">No orders here.</div>
      ) : (
        <div className="space-y-3">
          {list.map((o) => {
            const isOpen = !!expanded[o.id];
            return (
              <div key={o.id} className="rv-card rounded-xl" data-testid={`order-${o.id}`}>
                <button
                  onClick={() => setExpanded((e) => ({ ...e, [o.id]: !e[o.id] }))}
                  className="w-full flex items-center justify-between p-5 text-left"
                  data-testid={`order-toggle-${o.id}`}
                >
                  <div className="min-w-0">
                    <div className="font-display uppercase tracking-tight text-base truncate">
                      {o.values.discord || o.values.email || o.id.slice(0, 8)}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-white/45 mt-1">
                      {new Date(o.created_at).toLocaleString()}
                    </div>
                  </div>
                  {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </button>

                {isOpen && (
                  <div className="px-5 pb-5 border-t border-white/5">
                    <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3 mt-4">
                      {o.field_snapshot.map((f) => (
                        <div key={f.id}>
                          <dt className="text-[10px] uppercase tracking-widest text-white/45">{f.label}</dt>
                          <dd className="text-sm text-white/90 whitespace-pre-wrap break-words mt-1">
                            {o.values[f.id] || <span className="text-white/30">—</span>}
                          </dd>
                        </div>
                      ))}
                    </dl>

                    <div className="flex gap-3 mt-6 flex-wrap">
                      {statusBtn(o)}
                      <button
                        onClick={() => remove(o.id)}
                        className="text-xs uppercase tracking-widest text-white/55 hover:text-[var(--rv-red)] inline-flex items-center gap-1.5 px-3 py-2"
                        data-testid={`order-delete-${o.id}`}
                      >
                        <Trash2 size={12} /> Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-12">
      {renderGroup("In progress", inProgress, "var(--rv-red)", (o) => (
        <button
          onClick={() => updateStatus(o.id, "completed")}
          className="btn-primary"
          data-testid={`order-complete-${o.id}`}
        >
          <Check size={14} /> Mark completed
        </button>
      ))}
      {renderGroup("Completed", completed, "#22c55e", (o) => (
        <button
          onClick={() => updateStatus(o.id, "in_progress")}
          className="btn-ghost"
          data-testid={`order-reopen-${o.id}`}
        >
          <RotateCcw size={14} /> Reopen
        </button>
      ))}
    </div>
  );
}

/* ---------------- Form Setup Tab ---------------- */
const FIELD_TYPES = [
  { value: "text", label: "Single line" },
  { value: "textarea", label: "Multi line" },
  { value: "email", label: "Email" },
  { value: "number", label: "Number" },
  { value: "yesno", label: "Yes / No" },
];
